import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem/MenuItem';
import Typography from 'apollo-react/components/Typography/Typography';
import Email from 'apollo-react-icons/Email';
import EmailRead from 'apollo-react-icons/EmailRead';
import Info from 'apollo-react-icons/Info';
import StatusDotSolid from 'apollo-react-icons/StatusDotSolid';
import Trash from 'apollo-react-icons/Trash';
import classNames from 'classnames';
import moment from 'moment';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { NotificationLink } from './NotificationLink';
import { handleNotificationActions } from '../../../actions/NotificationsActions';
import { NOTIFICATION_ACTIONS } from '../../../constants/NotificationConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { DATE_FORMAT_WITH_MINUTES_TIMESTAMP } from '../../../utils/dateUtils';

const useStyles = makeStyles({
  itemMainContainer: {
    minHeight: 75,
    display: 'flex',
    position: 'relative',
    height: 'unset',
    alignItems: 'start !important',
  },
  mainContentContainer: {
    flexGrow: 1,
  },
  infoIcon: {
    marginLeft: 5,
    marginRight: 10,
    color: '#595959',
  },
  itemHeader: {
    padding: '0 !important',
    fontWeight: 600,
    fontSize: 17,
    maxWidth: 220,
    whiteSpace: 'pre-wrap',
    marginBottom: 5,
    display: 'block',
  },
  itemText: {
    whiteSpace: 'pre-wrap',
    color: '#595959',
    paddingRight: 10,
    fontSize: 15,
    lineHeight: 1.6,
  },
  itemDate: {
    position: 'absolute',
    top: 6,
    right: 10,
    color: '#595959',
    fontSize: 15,
  },
  iconsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  readIcon: {
    color: '#df216d',
    marginLeft: 5,
  },
  hiddenIcon: {
    visibility: 'hidden',
    color: '#df216d',
    marginLeft: 5,
  },
  actionContainer: {
    position: 'absolute',
    top: 2,
    right: 10,
    color: '#595959',
  },
  actionButton: {
    width: 32,
    height: 32,
    marginRight: 3,
  },
});

const NotificationItem = ({
  origin,
  title = 'Notification',
  text = 'Notification text',
  createdDate,
  createdBy,
  id,
  action,
  notificationExtensions,
  handleNotificationsClose,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const { actorCode } = useSelector((state) => state.auth);

  const [isItemActive, setItemActive] = useState(false);
  const [isActionUpdateLoading, setActionUpdateLoading] = useState(false);

  const dateValue = moment(createdDate).format(DATE_FORMAT_WITH_MINUTES_TIMESTAMP);
  const isNotificationRead = action === NOTIFICATION_ACTIONS.READ;
  const { Path: notificationUrl, ImportKey: importKey } = notificationExtensions;
  const shouldShowNotificationLink = actorCode === createdBy && notificationUrl;

  const handleMakeReadClick = async () => {
    const actionType =
      action === NOTIFICATION_ACTIONS.READ
        ? NOTIFICATION_ACTIONS.UNREAD
        : NOTIFICATION_ACTIONS.READ;

    setActionUpdateLoading(true);
    await dispatch(handleNotificationActions([{ Action: actionType, NotificationId: id }]));
    setActionUpdateLoading(false);
  };

  const handleDeleteClick = async () => {
    setActionUpdateLoading(true);
    await dispatch(
      handleNotificationActions([{ Action: NOTIFICATION_ACTIONS.DELETE, NotificationId: id }])
    );
    setActionUpdateLoading(false);
  };

  return (
    <MenuItem
      divider
      disableRipple
      onMouseEnter={() => setItemActive(true)}
      onMouseLeave={() => setItemActive(false)}
      className={classes.itemMainContainer}
    >
      <div className={classes.iconsContainer}>
        <StatusDotSolid
          fontSize="extraSmall"
          className={classNames(isNotificationRead ? classes.hiddenIcon : classes.readIcon)}
        />
        <Info className={classes.infoIcon} />
      </div>
      <div className={classes.mainContentContainer}>
        <span className={classes.itemHeader}>{translateString(title)}</span>
        <Typography className={classes.itemText}>{text}</Typography>
        {shouldShowNotificationLink && (
          <NotificationLink
            notificationUrl={notificationUrl}
            origin={origin}
            handleNotificationsClose={handleNotificationsClose}
            importKey={importKey}
          />
        )}
      </div>
      {isItemActive ? (
        <div className={classes.actionContainer}>
          <IconButton
            size="small"
            className={classes.actionButton}
            onClick={handleMakeReadClick}
            disabled={isActionUpdateLoading}
            aria-label={translateString(isNotificationRead ? 'Unread' : 'Read')}
          >
            {isNotificationRead ? <EmailRead /> : <Email />}
          </IconButton>
          <IconButton
            aria-label={translateString('Delete')}
            size="small"
            onClick={handleDeleteClick}
            className={classes.actionButton}
            disabled={isActionUpdateLoading}
          >
            <Trash />
          </IconButton>
        </div>
      ) : (
        <Typography className={classes.itemDate}>{dateValue}</Typography>
      )}
    </MenuItem>
  );
};

export default NotificationItem;
